import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import StateObserver, { user } from 'src/StateObserver';
import { ImageScaleView, ImageSliceView } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { parseAmount } from 'src/lib/utils';
import sounds from 'src/lib/sounds';
import { trackPinchEvent } from 'src/lib/analytics/events';
import { Feature } from 'src/replicant/analytics';
import * as Inventory from 'src/replicant/inventory';
import { startGemsPinchSequence } from 'src/game/shop/Helpers';
import { BoosterPanel } from './BoosterPanel';
import { Match2Booster } from 'src/replicant/match2/match2.ruleset';
import { getGameScene } from '../game/getGameScene';
import { DeepLink, createPopupDeeplink } from 'src/game/launch/deepLinks';
import { isDynamicTestInBucket } from 'src/replicant/getters/ab';
import ab from 'src/replicant/ruleset/ab';
import { createEmitter } from 'src/lib/Emitter';

import { AssetGroup } from '@play-co/timestep-core';

type CloseHandler = (result: boolean) => void;
const ui = uiConfig.buruburu.care.confirmation;

export default class PopupConfirmMatch2Booster extends PopupBasic {
  private close: CloseHandler;
  private cost: LangBitmapFontTextView;
  private balance: LangBitmapFontTextView;
  private okButton: ButtonScaleViewWithText;
  private match2Booster!: Match2Booster;

  constructor(opts: { superview: View; close: CloseHandler }) {
    const offset = 120;
    super({
      superview: opts.superview,
      close: () => opts.close(false),
      ...ui.popup,
      height: ui.popup.height - offset,
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PURCHASE_BOOSTER_POPUP,
      },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();
    this.message.removeFromSuperview();

    const costRow = this.box.addSubview(
      new ImageSliceView({ ...ui.costRow, y: ui.costRow.y - offset }),
    );

    costRow.addSubview(
      new LangBitmapFontTextView({
        ...ui.highlightRowLabel,
        localeText: () => i18n('purchaseWithGems.requiredGems'),
      }),
    );

    this.cost = new LangBitmapFontTextView({
      ...ui.highlightRowValue,
      superview: costRow,
    });

    costRow.addSubview(
      new ImageScaleView({
        ...ui.currency,
        image: 'assets/ui/hud/icons/hud_gem.png',
        x: costRow.style.width - ui.currency.width,
      }),
    );

    const balanceRow = this.box.addSubview(
      new ImageSliceView({ ...ui.balanceRow, y: ui.balanceRow.y - offset }),
    );

    balanceRow.addSubview(
      new LangBitmapFontTextView({
        ...ui.highlightRowLabel,
        localeText: () => i18n('purchaseWithGems.balanceAfterPurchase'),
      }),
    );

    this.balance = new LangBitmapFontTextView({
      ...ui.highlightRowValue,
      superview: balanceRow,
    });

    balanceRow.addSubview(
      new ImageScaleView({
        ...ui.currency,
        image: 'assets/ui/hud/icons/hud_gem.png',
        x: balanceRow.style.width - ui.currency.width,
      }),
    );

    this.box.addSubview(
      new ButtonScaleViewWithText({
        ...ui.backButton,
        y: ui.backButton.y - offset,
        localeText: () => i18n('basic.close'),
        onClick: async () => {
          sounds.sfx.play('cancel');
          return opts.close(false);
        },
        tracking: {
          feature: Feature.MATCH2._,
          $subFeature: Feature.MATCH2.PURCHASE_BOOSTER_BACK,
        },
      }),
    );

    this.okButton = new ButtonScaleViewWithText({
      ...ui.okButton,
      y: ui.okButton.y - offset,
      superview: this.box,
      localeText: () => i18n('basic.buy'),
      onClick: async () => {
        sounds.sfx.play('ok');
        return opts.close(true);
      },
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PURCHASE_BOOSTER_OK,
      },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup(['assets/ui/hud/icons/hud_gem.png']);
  }

  public init(booster: Match2Booster) {
    super.init({
      title: i18n(`match2.boosters.purchase.${booster.type}.title`),
    });

    this.match2Booster = booster;
    const price = booster.price;

    const boosterPanel = new BoosterPanel(booster);
    boosterPanel.updateOpts({
      x: this.box.style.width / 2,
      y: 150,
      centerOnOrigin: true,
    });
    this.box.addSubview(boosterPanel);

    this.cost.localeText = () => parseAmount(price);

    createEmitter(this.getView(), () => ({
      balance: this.getBalanceAfterPurchase(price),
    })).addListener(({ balance }) => {
      this.balance.localeText = () => parseAmount(balance);
      this.balance.updateOpts({
        color: this.canPurchase(price) ? '#9A7171' : 'red',
      });
    });

    this.okButton.onClick = async () => {
      sounds.sfx.play('ok');
      const balance = this.getBalanceAfterPurchase(price);
      const canPurchase = this.canPurchase(price);

      if (!canPurchase) {
        const gemsNeeded = balance < 0 ? Math.abs(balance) : 0;
        this.trackPinchEvent();

        const additionalContext: DeepLink[] = [];

        // Add this popup to the context, so it can be opened when switching to native.
        additionalContext.push({
          type: 'promptPurchaseBooster',
          boosterId: booster.type,
        });

        await startGemsPinchSequence({
          feature: 'match2',
          pinch: 'gems',
          gemsNeeded,
          additionalContext,
          crossplayTracking: {
            feature: 'puzzle_booster',
            subFeature: `puzzle_booster_${booster.type}`,
          },
        });
        return;
      }

      this.close(true);
    };
  }

  /**
   * Check if player can purchase care item
   */
  private canPurchase(price: number) {
    return this.getBalanceAfterPurchase(price) >= 0;
  }

  /**
   * Get balance after purchase, can be negative
   */
  private getBalanceAfterPurchase(price: number): number {
    return Inventory.getters.getGems(StateObserver.getState().user) - price;
  }

  private trackPinchEvent() {
    trackPinchEvent(
      {
        resource: 'gems',
        trigger: this.match2Booster.type,
      },
      getGameScene()?.getPinchAnalyticsData(),
    );
  }
}
